import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../../components/ui/table";
import { 
  Building2, Search, CreditCard, Calendar, Clock, Truck, Loader2,
  CheckCircle2, XCircle, AlertCircle, Plus, Settings, Zap, Crown,
  ArrowUpRight, History, RefreshCw
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatCurrency, formatDate } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PLAN_ICONS = {
  starter: Truck,
  professional: Zap,
  enterprise: Crown
};

const STATUS_BADGES = {
  trial: { label: "Prova", className: "bg-blue-100 text-blue-800" },
  active: { label: "Attivo", className: "bg-green-100 text-green-800" },
  expired: { label: "Scaduto", className: "bg-red-100 text-red-800" },
  cancelled: { label: "Cancellato", className: "bg-gray-100 text-gray-800" }
};

export default function AdminSubscriptions() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialog states
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showExtendTrialDialog, setShowExtendTrialDialog] = useState(false);
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [activateForm, setActivateForm] = useState({
    plan_id: "starter",
    months: 1,
    payment_method: "manual",
    notes: ""
  });
  const [extendForm, setExtendForm] = useState({ months: 1, payment_method: "manual", notes: "" });
  const [extendTrialForm, setExtendTrialForm] = useState({ days: 7 });
  const [changePlanForm, setChangePlanForm] = useState({ plan_id: "starter" });

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [companiesRes, plansRes] = await Promise.all([
        axios.get(`${API}/subscriptions/companies`, { headers, withCredentials: true }),
        axios.get(`${API}/subscriptions/plans`)
      ]);
      
      setCompanies(companiesRes.data);
      setPlans(plansRes.data.plans);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter((company) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!company.name.toLowerCase().includes(search) && 
          !company.email.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (statusFilter !== "all" && company.subscription_status !== statusFilter) {
      return false;
    }
    return true;
  });

  const openActivateDialog = (company) => {
    setSelectedCompany(company);
    setActivateForm({
      plan_id: company.subscription_plan || "starter",
      months: 1,
      payment_method: "manual",
      notes: ""
    });
    setShowActivateDialog(true);
  };

  const openExtendDialog = (company) => {
    setSelectedCompany(company);
    setExtendForm({ months: 1, payment_method: "manual", notes: "" });
    setShowExtendDialog(true);
  };

  const openExtendTrialDialog = (company) => {
    setSelectedCompany(company);
    setExtendTrialForm({ days: 7 });
    setShowExtendTrialDialog(true);
  };

  const openChangePlanDialog = (company) => {
    setSelectedCompany(company);
    setChangePlanForm({ plan_id: company.subscription_plan || "starter" });
    setShowChangePlanDialog(true);
  };

  const handleActivate = async () => {
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/subscriptions/company/${selectedCompany.company_id}/activate`,
        activateForm,
        { headers, withCredentials: true }
      );
      toast.success("Abbonamento attivato con successo");
      setShowActivateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'attivazione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtend = async () => {
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/subscriptions/company/${selectedCompany.company_id}/extend`,
        extendForm,
        { headers, withCredentials: true }
      );
      toast.success("Abbonamento esteso con successo");
      setShowExtendDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'estensione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtendTrial = async () => {
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/subscriptions/company/${selectedCompany.company_id}/extend-trial`,
        extendTrialForm,
        { headers, withCredentials: true }
      );
      toast.success("Periodo di prova esteso");
      setShowExtendTrialDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'estensione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePlan = async () => {
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(
        `${API}/subscriptions/company/${selectedCompany.company_id}/plan`,
        changePlanForm,
        { headers, withCredentials: true }
      );
      toast.success("Piano aggiornato con successo");
      setShowChangePlanDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio piano");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (companyId) => {
    if (!window.confirm("Sei sicuro di voler cancellare l'abbonamento di questa azienda?")) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/subscriptions/company/${companyId}/cancel`,
        {},
        { headers, withCredentials: true }
      );
      toast.success("Abbonamento cancellato");
      fetchData();
    } catch (error) {
      toast.error("Errore nella cancellazione");
    }
  };

  // Stats
  const stats = {
    total: companies.length,
    trial: companies.filter(c => c.subscription_status === "trial").length,
    active: companies.filter(c => c.subscription_status === "active").length,
    expired: companies.filter(c => c.subscription_status === "expired").length
  };

  return (
    <DashboardLayout title="Gestione Abbonamenti">
      <div className="space-y-6" data-testid="admin-subscriptions">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Totale Aziende</p>
                  <p className="text-2xl font-bold font-mono">{stats.total}</p>
                </div>
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Prova</p>
                  <p className="text-2xl font-bold font-mono text-blue-600">{stats.trial}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Attivi</p>
                  <p className="text-2xl font-bold font-mono text-green-600">{stats.active}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Scaduti</p>
                  <p className="text-2xl font-bold font-mono text-red-600">{stats.expired}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtra per stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="trial">In Prova</SelectItem>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="expired">Scaduti</SelectItem>
              <SelectItem value="cancelled">Cancellati</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Companies Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="py-12 text-center">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna azienda trovata</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Azienda</TableHead>
                    <TableHead>Piano</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead>Veicoli</TableHead>
                    <TableHead>Giorni</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((company) => {
                    const plan = plans[company.subscription_plan] || plans.starter;
                    const PlanIcon = PLAN_ICONS[company.subscription_plan] || Truck;
                    const statusBadge = STATUS_BADGES[company.subscription_status] || STATUS_BADGES.trial;
                    
                    return (
                      <TableRow key={company.company_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{company.name}</p>
                            <p className="text-sm text-muted-foreground">{company.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <PlanIcon className="h-4 w-4 text-primary" />
                            <span className="font-medium">{plan?.name || "Starter"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {company.subscription_status === "trial" 
                            ? (company.trial_end ? formatDate(company.trial_end) : "N/A")
                            : (company.subscription_end ? formatDate(company.subscription_end) : "N/A")
                          }
                        </TableCell>
                        <TableCell>
                          <span className="font-mono">
                            {company.vehicles_count || 0}
                            <span className="text-muted-foreground">
                              /{plan?.max_vehicles === -1 ? "∞" : plan?.max_vehicles || 5}
                            </span>
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`font-mono font-bold ${
                            company.days_remaining <= 7 ? "text-red-600" : 
                            company.days_remaining <= 14 ? "text-amber-600" : ""
                          }`}>
                            {company.days_remaining || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {company.subscription_status === "trial" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openActivateDialog(company)}
                                  title="Attiva abbonamento"
                                >
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openExtendTrialDialog(company)}
                                  title="Estendi prova"
                                >
                                  <Clock className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {company.subscription_status === "active" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openExtendDialog(company)}
                                  title="Estendi abbonamento"
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openChangePlanDialog(company)}
                                  title="Cambia piano"
                                >
                                  <ArrowUpRight className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {company.subscription_status === "expired" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openActivateDialog(company)}
                                title="Riattiva abbonamento"
                              >
                                <CreditCard className="h-4 w-4" />
                              </Button>
                            )}
                            {company.subscription_status !== "cancelled" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleCancel(company.company_id)}
                                title="Cancella abbonamento"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Activate Subscription Dialog */}
        <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Attiva Abbonamento</DialogTitle>
              <DialogDescription>
                Attiva un abbonamento per {selectedCompany?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Piano</Label>
                <Select 
                  value={activateForm.plan_id} 
                  onValueChange={(v) => setActivateForm({...activateForm, plan_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(plans).map(([id, plan]) => (
                      <SelectItem key={id} value={id}>
                        {plan.name} - €{plan.price}/mese
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durata (mesi)</Label>
                <Select 
                  value={String(activateForm.months)} 
                  onValueChange={(v) => setActivateForm({...activateForm, months: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 mese</SelectItem>
                    <SelectItem value="3">3 mesi</SelectItem>
                    <SelectItem value="6">6 mesi</SelectItem>
                    <SelectItem value="12">12 mesi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Metodo di Pagamento</Label>
                <Select 
                  value={activateForm.payment_method} 
                  onValueChange={(v) => setActivateForm({...activateForm, payment_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manuale</SelectItem>
                    <SelectItem value="bank_transfer">Bonifico</SelectItem>
                    <SelectItem value="cash">Contanti</SelectItem>
                    <SelectItem value="stripe">Stripe (Carta)</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea
                  value={activateForm.notes}
                  onChange={(e) => setActivateForm({...activateForm, notes: e.target.value})}
                  placeholder="Note opzionali..."
                />
              </div>
              <div className="bg-muted p-4">
                <p className="text-sm text-muted-foreground">Totale da pagare:</p>
                <p className="text-2xl font-bold font-mono">
                  €{(plans[activateForm.plan_id]?.price || 10) * activateForm.months}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleActivate} disabled={submitting} className="btn-primary">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Attiva"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Extend Subscription Dialog */}
        <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Estendi Abbonamento</DialogTitle>
              <DialogDescription>
                Estendi abbonamento per {selectedCompany?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Durata (mesi)</Label>
                <Select 
                  value={String(extendForm.months)} 
                  onValueChange={(v) => setExtendForm({...extendForm, months: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 mese</SelectItem>
                    <SelectItem value="3">3 mesi</SelectItem>
                    <SelectItem value="6">6 mesi</SelectItem>
                    <SelectItem value="12">12 mesi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Metodo di Pagamento</Label>
                <Select 
                  value={extendForm.payment_method} 
                  onValueChange={(v) => setExtendForm({...extendForm, payment_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manuale</SelectItem>
                    <SelectItem value="bank_transfer">Bonifico</SelectItem>
                    <SelectItem value="cash">Contanti</SelectItem>
                    <SelectItem value="stripe">Stripe (Carta)</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea
                  value={extendForm.notes}
                  onChange={(e) => setExtendForm({...extendForm, notes: e.target.value})}
                  placeholder="Note opzionali..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExtendDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleExtend} disabled={submitting} className="btn-primary">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Estendi"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Extend Trial Dialog */}
        <Dialog open={showExtendTrialDialog} onOpenChange={setShowExtendTrialDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Estendi Periodo di Prova</DialogTitle>
              <DialogDescription>
                Estendi il periodo di prova per {selectedCompany?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Giorni da aggiungere</Label>
                <Select 
                  value={String(extendTrialForm.days)} 
                  onValueChange={(v) => setExtendTrialForm({days: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="14">14 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExtendTrialDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleExtendTrial} disabled={submitting} className="btn-primary">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Estendi"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Plan Dialog */}
        <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cambia Piano</DialogTitle>
              <DialogDescription>
                Cambia il piano per {selectedCompany?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nuovo Piano</Label>
                <Select 
                  value={changePlanForm.plan_id} 
                  onValueChange={(v) => setChangePlanForm({plan_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(plans).map(([id, plan]) => (
                      <SelectItem key={id} value={id}>
                        {plan.name} - €{plan.price}/mese ({plan.max_vehicles === -1 ? "illimitati" : plan.max_vehicles} veicoli)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-muted-foreground">
                Nota: Cambiare il piano modifica solo il tipo di abbonamento. La data di scadenza rimane invariata.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChangePlanDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleChangePlan} disabled={submitting} className="btn-primary">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cambia Piano"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
